import { useEffect, useRef, useState } from 'react';

import { type TProductEdition as ProductEdition } from '@atlassian/search-client/fetch-search-config';
import {
	getTenantSmartlinkUsageAndTopApps,
	type SmartlinkUsageAndTopApps,
} from '@atlassian/search-client/smartlink-usage-and-top-apps';

import { type PrimaryProductKey } from '../../common/constants/product-keys';
import { type ThirdPartyConfigsBootstrap } from '../../common/constants/schemas/3p-config';
import { type SearchStoreExperience } from '../../common/constants/search';
import {
	onSmartLinkUsageAndTopAppsFetchFail,
	onSmartLinkUsageAndTopAppsFetchSuccess,
} from '../../common/utils/events/smart-link-usage-and-top-apps-events';
import { serveFPSGitHubSocialProofBannerExperiment } from '../../common/utils/experiments';
import { useAnalytics } from '../analytics';

/**
 * Tenant editions covered by the underlying TAP site trait
 * (`rovo_nudge_end_user_rendered_smartlinks_percent_and_most_used_apps`).
 * The trait is only computed for MAU customers on paid editions; calling the
 * endpoint for tenants outside this set just returns an empty payload, so we
 * skip the fetch entirely.
 */
const COVERED_EDITIONS: ReadonlySet<string> = new Set(['STANDARD', 'PREMIUM', 'ENTERPRISE']);

const isPrimaryProductCovered = (
	primaryProduct: PrimaryProductKey,
	productEdition: ProductEdition | undefined,
): boolean => {
	const edition = productEdition?.[primaryProduct];
	return !!edition && COVERED_EDITIONS.has(edition.toUpperCase());
};

/**
 * Hook that fetches the site-scoped Smart Link usage % and top apps trait.
 *
 * The trait is only fetched when:
 *   1. The user is eligible for (and bucketed into the test cohort of) the
 *      `fps_github_social_proof_banner` experiment (checked with
 *      `fireExperimentExposure: false` so this hook does not fire an exposure).
 *   2. The primary product is on a paid edition (Standard / Premium /
 *      Enterprise) — the trait only covers MAU customers on paid editions.
 *
 * NOTE: `thirdPartyConfigs` and `productEdition` are taken as parameters rather
 * than read from the bootstrap store. This hook runs inside
 * `BootstrapStoreProvider`, which is the producer of those store values —
 * reading from the store here would always return the default empty values
 * because the store has not yet been seeded with the freshly-fetched config.
 * Pass the values straight from `useFetchConfig`.
 */
export const useFetchSmartLinkUsageAndTopApps = (
	experience: SearchStoreExperience,
	primaryProduct: PrimaryProductKey,
	cloudId?: string,
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap,
	productEdition?: ProductEdition,
): SmartlinkUsageAndTopApps | undefined => {
	const [smartLinkUsageAndTopApps, setSmartLinkUsageAndTopApps] = useState<
		SmartlinkUsageAndTopApps | undefined
	>(undefined);
	const { fireAnalyticsEvent } = useAnalytics({ experience });
	// Ensure we only ever trigger the fetch once per mount, even if
	// `thirdPartyConfigs` / `productEdition` (and therefore
	// `isFPSGitHubSocialProofBannerEnabled` / `isCoveredTenant`) later change —
	// the trait is site-scoped and doesn't change within a session.
	const hasFetchedRef = useRef(false);
	// NO EXPOSURE! Allocate to short-circuit the hook if the experiment is disabled
	// or the user is not eligible.
	const isFPSGitHubSocialProofBannerEnabled = serveFPSGitHubSocialProofBannerExperiment(
		{ thirdPartyConfigs },
		{ fireExperimentExposure: false },
	);
	const isCoveredTenant = isPrimaryProductCovered(primaryProduct, productEdition);

	useEffect(() => {
		if (
			isFPSGitHubSocialProofBannerEnabled &&
			isCoveredTenant &&
			cloudId &&
			!hasFetchedRef.current
		) {
			hasFetchedRef.current = true;
			getTenantSmartlinkUsageAndTopApps({ siteId: cloudId })
				.then((data) => {
					setSmartLinkUsageAndTopApps(data);
					if (data) {
						fireAnalyticsEvent(
							onSmartLinkUsageAndTopAppsFetchSuccess({
								cloudId,
								experience,
								percentage: data.percentage,
							}),
						);
					}
				})
				.catch((error) => {
					fireAnalyticsEvent(
						onSmartLinkUsageAndTopAppsFetchFail({
							cloudId,
							experience,
							reason: error instanceof Error ? error.message : String(error),
						}),
					);
				});
		}
	}, [
		isFPSGitHubSocialProofBannerEnabled,
		isCoveredTenant,
		cloudId,
		experience,
		fireAnalyticsEvent,
	]);

	return smartLinkUsageAndTopApps;
};
